import React, { useState, useEffect, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useTheme,
  Switch,
} from "@mui/material";
import CapitalizeText from "../utils/capitalise";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDollarSign } from "@fortawesome/free-solid-svg-icons";
import { ArrowDownward, ArrowUpward } from "@mui/icons-material";
import axios from "axios";
import { estPayant } from "../utils/estPayant";

/**
 * PluginTable
 * @param {Array} sortedData - la liste brute (ex. [{plugins:[...]}])
 * @param {Function} handleRowClick(index) => ouvre le Drawer
 * @param {Function} onDisplayedPluginsChange => callback pour remonter la liste finale
 */
const PluginTable = ({ sortedData = [], handleRowClick, onDisplayedPluginsChange = {}, role, isAdmin, updatePluginPayant }) => {
  const theme = useTheme();
  // 1) Aplatir les plugins pour avoir un tableau simple
  // useMemo obligatoire : sans lui l'identité du tableau change à chaque render
  // et l'effet de filtrage/tri (dépendant de flattenedData) boucle indéfiniment.
  const flattenedData = useMemo(
    () =>
      sortedData.flatMap((groupItem) =>
        (groupItem.plugins || []).map((plugin) => {
          // Récupération des "services"
          const allServices = (plugin.sites || [])
            .map((site) => site.selectedService || site["selected-service"])
            .filter(Boolean);

          // Retrait des doublons
          const uniqueServices = [...new Set(allServices)];

          // On stocke tout dans un array
          return {
            ...plugin,
            occurence: (plugin.sites || []).length, // nb de sites avant filtrage
            servicesArray: uniqueServices,
          };
        })
      ),
    [sortedData]
  );

  // 2) Récupérer tous les services distincts => dropdown
  const allServices = useMemo(() => {
    const serviceSet = new Set();
    flattenedData.forEach((plugin) => {
      (plugin.servicesArray || []).forEach((svc) => serviceSet.add(svc));
    });
    // Ajout de "All" en tête
    return ["All", ...Array.from(serviceSet).sort()];
  }, [flattenedData]);

  // 3) États
  // - Filtrage par service
  const [serviceFilter, setServiceFilter] = useState("All");

  // - Tri "payant"
  const [sortColumn, setSortColumn] = useState(null); // "payant" etc.
  const [sortOrder, setSortOrder] = useState("asc");

  // - Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // - Liste finale
  const [displayedPlugins, setDisplayedPlugins] = useState(flattenedData);


  const isDark = theme.palette.mode === "dark";

  const isOnet = role === "onet";

  const isCompass = role === "compass"

  const togglePayant = async (plugin, e) => {
    e.stopPropagation();
    const token = sessionStorage.getItem("token");
    const slug = plugin.slug || plugin.name;
    const newValue = !estPayant(plugin);
    try {
      await axios.put(
        `${process.env.REACT_APP_DOMAIN_URL}/api/plugins/${encodeURIComponent(slug)}/isPayant`,
        { payant: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      updatePluginPayant(slug, newValue);
    } catch (err) {
      console.error("Erreur mise à jour payant:", err);
    }
  };

  const normalizeVendorKey = (name) => {
    return name?.toString().trim().toLowerCase();
  };

  /**
   * Tri sur "payant"
   * @param {Array} list
   * @param {String} column
   * @param {String} order
   */
  const sortPlugins = (list, column, order) => {
    // On ne trie que si la colonne est "payant"
    if (column !== "payant") return list;

    return [...list].sort((a, b) => {
      const aPayant = estPayant(a);
      const bPayant = estPayant(b);
      if (aPayant === bPayant) return 0;

      // tri booléen
      if (order === "asc") {
        return aPayant ? 1 : -1;
      } else {
        return aPayant ? -1 : 1;
      }
    });
  };

  /**
   * Gère le clic sur une colonne triable
   * ex : "payant" => alterne asc / desc
   */
  const handleSortColumn = (col) => {
    let newOrder = "asc";
    if (sortColumn === col && sortOrder === "asc") {
      newOrder = "desc";
    }
    setSortColumn(col);
    setSortOrder(newOrder);
  };

  // 4) Filtrage + Tri => displayedPlugins
  useEffect(() => {
    let newList = [...flattenedData];

    // Le backend filtre déjà les plugins selon le viewRole
    // On garde uniquement le filtre de service pour le cas où l'utilisateur le modifie manuellement
    // A) Filtrage par service (si différent de "All")
    if (serviceFilter !== "All") {
      // On ne garde que les plugins qui contiennent ce service
      newList = newList.filter((pl) =>
        (pl.servicesArray || []).some(
          (svc) => svc.toLowerCase() === serviceFilter.toLowerCase()
        )
      );

      // Et on ne garde dans pl.sites QUE les sites correspondants
      newList = newList.map((plugin) => {
        const filteredSites = (plugin.sites || []).filter((site) => {
          const siteSvc = site.selectedService || site["selected-service"];
          return siteSvc?.toLowerCase() === serviceFilter.toLowerCase();
        });
        return {
          ...plugin,
          sites: filteredSites,
          occurence: filteredSites.length,
        };
      });
    }

    // B) Tri "payant"
    if (sortColumn) {
      newList = sortPlugins(newList, sortColumn, sortOrder);
    }

    // on met à jour displayedPlugins
    setDisplayedPlugins(newList);

    // Attention : on NE force PAS currentPage = 1
    // => ainsi la pagination est conservée
  }, [flattenedData, serviceFilter, sortColumn, sortOrder]);

  // => On remonte displayedPlugins au parent (pour le Drawer)
  useEffect(() => {
    onDisplayedPluginsChange(displayedPlugins);
  }, [displayedPlugins, onDisplayedPluginsChange]);

  // 5) Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = displayedPlugins.slice(indexOfFirstItem, indexOfLastItem);

  // Handlers
  const handleServiceFilterChange = (e) => {
    setServiceFilter(e.target.value);
    // Option : si vous VOULEZ repasser à la page 1 quand on change de filtre, décommentez :
    // setCurrentPage(1);
  };

  const handleChangePage = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const handleChangeItemsPerPage = (event) => {
    setItemsPerPage(event.target.value);
    setCurrentPage(1); // on revient page 1 après changer "itemsPerPage"
  };

  // 6) Rendu
  return (
    <TableContainer component={Paper} sx={{ borderRadius: 2,borderTopLeftRadius: 0, overflow: 'hidden', boxShadow:2 }}>
      <Table>
        <TableHead>
          <TableRow>
            {/* Nom du plugin */}
            <TableCell style={{ width: "300px" }}>Nom</TableCell>

            {/* Dernière version */}
            <TableCell>Dernière version</TableCell>

            {/* Nb site */}
            <TableCell align="right">Nb site</TableCell>

            {/* Dropdown Service */}
            {role === "admin" && (
            <TableCell>
              <FormControl variant="outlined" size="small">
                <InputLabel id="service-filter-label">Service</InputLabel>
                <Select
                  labelId="service-filter-label"
                  value={serviceFilter}
                  label="Service"
                  onChange={handleServiceFilterChange}
                  style={{ minWidth: 120 }}
                >
                  {allServices.map((svc) => (
                    <MenuItem key={svc} value={svc}>
                      {svc}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </TableCell>)}
            {/* Colonne triable "Payant" */}
            <TableCell
              align="right"
              style={{
                cursor: "pointer",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                borderBottom: "none",
                marginTop:"8px"
              }}
              onClick={() => handleSortColumn("payant")}
            >
              Payant
              {sortColumn === "payant" &&
                (sortOrder === "asc" ? (
                  <ArrowUpward fontSize="small" style={{ marginLeft: 5 }} />
                ) : (
                  <ArrowDownward fontSize="small" style={{ marginLeft: 5 }} />
                ))}
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {currentItems.map((plugin, index) => {
            const vendorKey = normalizeVendorKey(plugin.name);

            return (
              <TableRow
                key={`${vendorKey}-${index}`}
                sx={{
                  backgroundColor: index % 2 === 0
                    ? theme.palette.background.alternate
                    : theme.palette.background.paper,
                  '&:hover': {
                    backgroundColor: isDark
                      ? 'rgba(255, 255, 255, 0.1)'
                      : 'rgba(0, 0, 0, 0.04)',
                  },
                  cursor: 'pointer',
                }}
                onClick={() => handleRowClick(indexOfFirstItem + index)}
              >
                <TableCell>{plugin.name}</TableCell>
                <TableCell>{plugin.lastVersionPLUG}</TableCell>
                <TableCell align="right">{plugin.occurence}</TableCell>

                {/* Afficher la liste de services ou la valeur filtrée */}
                {role === "admin" && (
                <TableCell>
                  {serviceFilter === "All"
                    ? (plugin.servicesArray || []).join(", ")
                    : serviceFilter}
                </TableCell>)}


                {/* Payant ou non */}
                <TableCell align="right" style={{ fontWeight: "bold" }}>
                  {estPayant(plugin) ? <FontAwesomeIcon icon={faDollarSign} /> : "-"}
                  {isAdmin && (
                    <Switch
                      size="small"
                      checked={estPayant(plugin)}
                      onChange={(e) => togglePayant(plugin, e)}
                      onClick={(e) => e.stopPropagation()}
                      sx={{ ml: 1 }}
                    />
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {/* Pagination + Choix d'items par page */}
      <Stack
        spacing={2}
        direction="row"
        justifyContent="center"
        alignItems="center"
        padding={2}
      >
        <FormControl variant="outlined" size="small">
          <InputLabel sx={{
                              color: isDark ? theme.palette.text.primary : 'black',
                              '&.Mui-focused': {
                                color: isDark ? theme.palette.text.primary : 'black',
                              },
                            }} id="items-per-page-label">Plugins</InputLabel>
          <Select
            labelId="items-per-page-label"
            value={itemsPerPage}
            label="Plugins"
            onChange={handleChangeItemsPerPage}
            sx={{
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: isOnet
                  ? '#06305C'
                  : isCompass
                    ? '#c4ac66'
                    : isDark
                      ? 'rgba(255, 255, 255, 0.23)'
                      : '#F7E900',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: isOnet ? '#06305C' : role === 'compass' ? '#c4ac66' : '#F7E900',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root:hover': {
                    backgroundColor: isOnet
                      ? 'rgba(6, 48, 92, 0.08)'
                      : isCompass
                        ? 'rgba(196, 172, 102, 0.08)'
                        : isDark
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(247, 233, 0, 0.08)',
                  },
                  '& .MuiMenuItem-root.Mui-selected': {
                    backgroundColor: isOnet
                      ? 'rgba(6, 48, 92, 0.12)'
                      : isCompass
                        ? 'rgba(196, 172, 102, 0.12)'
                        : isDark
                          ? 'rgba(255, 255, 255, 0.08)'
                          : 'rgba(247, 233, 0, 0.12)',
                  },
                  '& .MuiMenuItem-root.Mui-selected:hover': {
                    backgroundColor: isOnet
                      ? 'rgba(6, 48, 92, 0.16)'
                      : isCompass
                        ? 'rgba(196, 172, 102, 0.16)'
                        : isDark
                          ? 'rgba(255, 255, 255, 0.12)'
                          : 'rgba(247, 233, 0, 0.16)',
                  },
                },
              },
            }}
          >
            <MenuItem  value={5}>5</MenuItem>
            <MenuItem value={10}>10</MenuItem>
            <MenuItem value={50}>50</MenuItem>
          </Select>
        </FormControl>

        <Pagination
          count={Math.max(1, Math.ceil(displayedPlugins.length / itemsPerPage))}
          page={currentPage}
          onChange={handleChangePage}
          variant="outlined"
          sx={{
            "& .MuiPaginationItem-root": {
              backgroundColor: isOnet
                ? (isDark ? theme.palette.background.paper : '')
                : (isDark ? theme.palette.background.paper : "white"),
              color: isDark ? theme.palette.text.primary : "black",
              "&.Mui-selected": {
                backgroundColor: isOnet ? '#06305C' : isCompass ? '#c4ac66' : "rgba(247, 233, 0, 0.4)",
                border: isOnet
                  ? "1px solid"
                  : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
                color: isOnet ? "white" : isCompass ? "white" : (isDark ? "white" : "black"),
              },
              "&:hover": {
                backgroundColor: isOnet
                  ? '#06305C'
                  : isCompass
                    ? '#c4ac66'
                    : (isDark ? "rgba(247, 233, 0, 0.2)" : "rgba(247, 233, 0, 0.4)"),
                color: isOnet
                  ? "white"
                  : isCompass
                    ? "white"
                    : (isDark ? "white" : "rgba(0, 0, 0, 0.23)"),
                border: isOnet
                  ? "1px solid"
                  : `1px solid ${isDark ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)'}`,
              },
            }
          }}
        />
      </Stack>
    </TableContainer>
  );
};

export default PluginTable;
